import moment from 'moment';

/**
 * Extracts tasks from capacity planning data structure.
 * Handles the new API response format where tasks are nested under allocated_task[resourceId].tasks
 * @param capacityPlanningData - The capacity planning response data
 * @returns Record<string, Record<string, any[]>> - Tasks organized by resourceId and date
 */
export const extractCapacityPlanningTasks = (capacityPlanningData: any): Record<string, any> => {
  const extractedTasks: Record<string, any> = {};

  if (capacityPlanningData?.allocated_task) {
    Object.keys(capacityPlanningData.allocated_task).forEach(resourceId => {
      const resourceData = capacityPlanningData.allocated_task[resourceId];
      if (resourceData?.tasks) {
        extractedTasks[resourceId] = resourceData.tasks;
      }
    });
  }

  return extractedTasks;
};

export const getOrgWorkingDays = (orgId: string) => {
  let workingDay = localStorage.getItem("workingDays") ? localStorage.getItem("workingDays") : ""
  let workingDays = workingDay ? JSON.parse(workingDay) : {};

  return workingDays[orgId] ? workingDays[orgId] : defaultWorkingDays;
}

export const isOrgWeekend = (date: string, orgWorkingDays: Object[]) => {
  const dayOfWeek = new Date(date).getDay();
  return !orgWorkingDays.includes(dayOfWeek);
};

export enum WEEKDAY {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
}

export const defaultWorkingDays = [WEEKDAY.MONDAY, WEEKDAY.TUESDAY, WEEKDAY.WEDNESDAY, WEEKDAY.THURSDAY, WEEKDAY.FRIDAY];

/**
 * Calculates the number of working days between two dates, taking into account
 * organization holidays and working days.
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @param organizationWorkingDays - Array of working days (1-7, where 1 is Monday)
 * @param organizationHolidays - Array of holiday objects with date property
 * @returns number of working days
 */
export const calculateWorkingDaysBetweenDates = (
  startDate: string,
  endDate: string,
  organizationWorkingDays: number[],
  organizationHolidays: { date: string }[]
): number => {
  const start = moment(startDate).startOf('day');
  const end = moment(endDate).startOf('day');
  let workingDays = 0;
  const current = moment(start);

  // Convert holidays to a set of date strings for faster lookup
  const holidayDates = new Set(
    organizationHolidays.map(h => h.date.split('T')[0])
  );

  // Count all days between start and end (inclusive)
  while (current.isSameOrBefore(end)) {
    const currentDate = current.format('YYYY-MM-DD');
    const isWeekend = isOrgWeekend(currentDate, organizationWorkingDays);
    const isHoliday = holidayDates.has(currentDate);

    if (!isWeekend && !isHoliday) {
      workingDays++;
    }
    current.add(1, 'day');
  }

  return Math.max(workingDays, 1); // Ensure minimum 1 day duration
};

// New: Signed working-day difference between two dates (skips weekends/holidays)
// Positive if toDate is after fromDate, negative if toDate is before fromDate, zero if same day.
export const calculateWorkingDayDifference = (
  fromDate: string,
  toDate: string,
  organizationWorkingDays: number[],
  organizationHolidays: { date: string }[]
): number => {
  const from = moment(fromDate).startOf('day');
  const to = moment(toDate).startOf('day');

  if (from.isSame(to, 'day')) return 0;

  const holidayDates = new Set(
    organizationHolidays.map(h => (h?.date || '').split('T')[0])
  );

  const isWorkingDay = (m: moment.Moment) => {
    const d = m.format('YYYY-MM-DD');
    const weekend = isOrgWeekend(d, organizationWorkingDays);
    const holiday = holidayDates.has(d);
    return !weekend && !holiday;
  };

  let count = 0;

  if (to.isAfter(from)) {
    const current = from.clone().add(1, 'day'); // exclusive of from, inclusive of to
    while (current.isSameOrBefore(to)) {
      if (isWorkingDay(current)) count++;
      current.add(1, 'day');
    }
    return count; // positive
  } else {
    const current = from.clone().subtract(1, 'day'); // exclusive of from, inclusive of to
    while (current.isSameOrAfter(to)) {
      if (isWorkingDay(current)) count++;
      current.subtract(1, 'day');
    }
    return -count; // negative
  }
};


export const capitalizeFirst = (str:string) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}